import { COUNTRIES } from '../components/country-tabs';
import type { ConnectionSummary } from '../hooks/use-connections';
import type {
  CarouselRunCardRow,
  CarouselRunRow,
  FormatId,
} from '../types/api';

/**
 * The Instagram render formats the publish page can post, in selector order.
 * `ig_story` is deliberately excluded — the Stories publishing path is not
 * built, so it is neither offered in the format selector nor published here.
 */
export const PUBLISHABLE_IG_FORMATS: readonly FormatId[] = [
  'ig_square',
  'ig_portrait',
];

/**
 * The distinct publishable Instagram formats present in a run, returned in
 * `PUBLISHABLE_IG_FORMATS` order. Drives the Square/Portrait selector — a run
 * with both yields a two-tab bar, a single-format run yields one entry.
 */
export function publishableFormats(
  cards: CarouselRunCardRow[],
): FormatId[] {
  const present = new Set(cards.map((c) => c.platform));
  return PUBLISHABLE_IG_FORMATS.filter((f) => present.has(f));
}

/**
 * The cards for one publishable format, in carousel order. Same filter + sort
 * the backend publisher applies to a single format, so the preview matches
 * exactly what that format would publish.
 */
export function cardsForFormat(
  cards: CarouselRunCardRow[],
  format: FormatId,
): CarouselRunCardRow[] {
  return cards
    .filter((c) => c.platform === format)
    .sort((a, b) => a.card_index - b.card_index);
}

export interface TargetAccount {
  /** 'global' for the brand account, otherwise an uppercase ISO alpha-2 code. */
  countryKey: string;
  label: string;
  flag: string;
  handle: string | null;
  connected: boolean;
}

/**
 * Resolves which Instagram account a run publishes through, from the run's
 * country. `runCountry === null` → the global brand account. `connections` is
 * the (already country-scoped) list from `useConnections`.
 */
export function resolveTargetAccount(
  runCountry: string | null,
  connections: ConnectionSummary[],
): TargetAccount {
  const countryKey = runCountry ?? 'global';
  const meta = COUNTRIES.find((c) => c.code === countryKey);
  const match = connections.find(
    (c) => c.platform === 'instagram' && (c.country ?? 'global') === countryKey,
  );
  return {
    countryKey,
    label: meta?.label ?? countryKey,
    flag: meta?.flag ?? '🌐',
    handle: match?.accountHandle ?? null,
    connected: Boolean(match),
  };
}

export interface PublishReadiness {
  ready: boolean;
  blockers: string[];
}

/**
 * Every reason a run cannot be published right now. Empty `blockers` → ready.
 */
export function publishReadiness(args: {
  status: CarouselRunRow['status'];
  renderedAt: string | null;
  captionPresent: boolean;
  igCardCount: number;
  connected: boolean;
}): PublishReadiness {
  const blockers: string[] = [];
  // A run is publishable once its assets have rendered. `rendered_at` is the
  // authoritative signal: a run that failed at the publish step still has its
  // cards in storage, so it can be published manually. Only a run that never
  // finished rendering (no `rendered_at`, and not in a rendered/published
  // status) is blocked here.
  const hasRendered =
    args.status === 'rendered' ||
    args.status === 'published' ||
    args.renderedAt !== null;
  if (!hasRendered) {
    blockers.push(
      `Run is ${args.status} — it has not finished rendering yet.`,
    );
  }
  if (!args.captionPresent) {
    blockers.push(
      'No caption stored — re-render this carousel before publishing.',
    );
  }
  if (args.igCardCount === 0) {
    blockers.push('No Instagram cards in this run.');
  }
  if (!args.connected) {
    blockers.push('No Instagram account connected for this country.');
  }
  return { ready: blockers.length === 0, blockers };
}
